import { reportError } from '@/utils/tracking';
import { datadogRum } from '@datadog/browser-rum';
import moment, { Moment } from 'moment-timezone';

// A dynamic import fails when the chunk it names is no longer on the server, which is the
//  case for any page that was loaded before a deploy changed the chunk hashes. Reloading
//  fetches the current index.html and the chunks it points at.
// The same import also fails when the client cannot reach the server at all, and a reload
//  fixes nothing there, so attempts are spaced out to keep such a client from reloading in
//  a loop.
const MIN_MINUTES_BETWEEN_RELOAD_ATTEMPTS = 2;
const RELOAD_ATTEMPT_STORAGE_KEY = 'lastModuleFetchReloadAttempt';

// Browsers phrase a failed dynamic import differently:
//  - Chrome:  "Failed to fetch dynamically imported module: <url>"
//  - Safari:  "Importing a module script failed."
//  - Firefox: "error loading dynamically imported module"
const MODULE_FETCH_ERROR_MESSAGE =
    /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i;

declare global {
    interface WindowEventMap {
        'vite:preloadError': Event & { payload: unknown };
    }
}

export const isModuleFetchError = (error: unknown): boolean =>
    error instanceof TypeError && MODULE_FETCH_ERROR_MESSAGE.test(error.message);

export const isReloadAttemptDue = ({
    lastAttempt,
    now,
}: {
    lastAttempt: Moment | null;
    now: Moment;
}): boolean => {
    if (lastAttempt === null) {
        return true;
    }

    return now.diff(lastAttempt, 'minutes', true) >= MIN_MINUTES_BETWEEN_RELOAD_ATTEMPTS;
};

const readLastReloadAttempt = (): Moment | null => {
    const storedValue = window.sessionStorage.getItem(RELOAD_ATTEMPT_STORAGE_KEY);
    if (!storedValue) {
        return null;
    }

    const lastAttempt = moment(storedValue);
    return lastAttempt.isValid() ? lastAttempt : null;
};

// The name a recovered chunk fetch is recorded under in RUM.
export const MODULE_PRELOAD_RECOVERY_ACTION = 'module preload recovered by reload';

export const isModuleFetchReloadDue = (): boolean =>
    isReloadAttemptDue({ lastAttempt: readLastReloadAttempt(), now: moment() });

const startReload = () => {
    window.sessionStorage.setItem(RELOAD_ATTEMPT_STORAGE_KEY, moment().toJSON());
    window.location.reload();
};

// Reloads the page unless the previous attempt was too recent.
export const tryReloadingAfterModuleFetchFailure = () => {
    if (!isModuleFetchReloadDue()) {
        return;
    }

    startReload();
};

// Vite's preload helper dispatches this event when it cannot fetch a dynamically imported
//  chunk or that chunk's CSS. Reloading from here starts the recovery as soon as the fetch
//  fails, without waiting for the rejection to travel up through React.
// The event is cancelable, and canceling it is the wrong move: the helper then resolves the
//  import with `undefined` instead of rethrowing, so an importer that reads a named export off
//  the resolved module fails with a confusing "cannot read properties of undefined". Leaving
//  the event alone keeps the original error, which the error boundaries know how to handle
//  when the reload is skipped.
// The helper only exists in built output. The dev server uses native dynamic import and never
//  dispatches the event.
export const listenForModulePreloadErrors = () => {
    window.addEventListener('vite:preloadError', (event) => {
        // A failure the reload fixes is the ordinary shape of a client on an old deploy, so
        //  it is recorded as an action. Reporting it as an error puts it in the same bucket
        //  as a failure nobody recovered from, which is the case worth looking at.
        if (!isModuleFetchReloadDue()) {
            reportError(event.payload, { isModulePreloadError: true, reloadSkipped: true });
            return;
        }

        datadogRum.addAction(MODULE_PRELOAD_RECOVERY_ACTION);
        startReload();
    });
};
